import React, { RefObject } from 'react';
import type { FieldValidator } from 'final-form';
import { Field, FieldRenderProps, FieldProps } from 'react-final-form';
import InputFieldV3 from '../InputFieldV3';
import transformValidateProp from '../../util/transformValidateProp';
import { InputFieldV3Props } from '../InputFieldV3/InputFieldV3';

export interface FinalFormInputProps<
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
> extends Omit<FieldProps<string, RP, T>, 'validate' | 'type'>,
    InputFieldV3Props {
  name: string;
  validate?: FieldValidator<string>[] | FieldValidator<string> | null;
  inputRef?: RefObject<HTMLInputElement>;
}

/**
 *
 * ```import FinalFormInput from '@zola/zola-ui/build/es/components/Form/input/FinalFormInput';```
 *
 * Renders a text, password, tel, etc in React Final Form using the Design System.
 *
 * This is a wrapper for the `<input type=` HTML element so it can render
 * passwords, text, or other fields that are `<input>` elements.  By default,
 * it will render a text box.
 *
 */
const FinalFormInput = <
  RP extends FieldRenderProps<string, T>,
  T extends HTMLElement = HTMLElement
>(
  props: FinalFormInputProps<RP, T>
): React.ReactElement => {
  const { validate, type = 'text', inputRef, ...rest } = props;
  const composedValidators = transformValidateProp(validate);

  return (
    <Field validate={composedValidators} type={type} {...rest}>
      {({ input, meta, onChange: onChangeFct, onBlur: onBlurFct, ...fieldProps }) => {
        const hasError = meta && meta.touched && (meta.submitError || meta.error);
        const errorMessage = hasError ? meta.submitError || meta.error : '';
        const inputProps = {
          ...input,
          ...fieldProps,
          onChange: (e: React.ChangeEvent<HTMLInputElement>) => {
            input.onChange(e);
            if (onChangeFct) {
              onChangeFct(e);
            }
          },
          onBlur: (e: React.FocusEvent<HTMLInputElement>) => {
            input.onBlur(e);
            if (onBlurFct) {
              onBlurFct(e);
            }
          },
          errorMessage,
          isControlled: true,
        };
        return <InputFieldV3 ref={inputRef} {...inputProps} />;
      }}
    </Field>
  );
};

export default FinalFormInput;
